import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  InputNumber,
  Switch,
  message,
  Popconfirm,
  Radio,
  Upload,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;

interface ContentType {
  id: number;
  name: string;
  code: string;
  category: 'video' | 'image' | 'text' | 'audio';
  description: string;
  template: string;
  duration?: number; // 视频时长（秒）
  resolution?: string; // 分辨率
  aspectRatio?: string; // 宽高比
  tags: string[];
  status: 'active' | 'inactive';
  priority: number;
  createTime: string;
  updateTime: string;
}

// Demo数据
const initialData: ContentType[] = [
  {
    id: 1,
    name: '抖奶擦边视频',
    code: 'bounce_edge_video',
    category: 'video',
    description: '性感擦边视频内容，吸引用户关注',
    template: 'bounce_template_v1',
    duration: 15,
    resolution: '1080x1920',
    aspectRatio: '9:16',
    tags: ['擦边', '性感', '吸引'],
    status: 'active',
    priority: 1,
    createTime: '2025-12-01 10:00:00',
    updateTime: '2025-12-01 10:00:00',
  },
  {
    id: 2,
    name: '足球对战分析视频',
    code: 'football_analysis_video',
    category: 'video',
    description: '今日足球对战分析报告视频',
    template: 'football_analysis_v1',
    duration: 60,
    resolution: '1920x1080',
    aspectRatio: '16:9',
    tags: ['体育', '分析', '专业'],
    status: 'active',
    priority: 2,
    createTime: '2025-12-01 10:00:00',
    updateTime: '2025-12-01 10:00:00',
  },
  {
    id: 3,
    name: '美女直播预告图',
    code: 'beauty_live_preview',
    category: 'image',
    description: '美女主播直播预告图片',
    template: 'beauty_preview_v1',
    resolution: '1080x1920',
    aspectRatio: '9:16',
    tags: ['美女', '直播', '预告'],
    status: 'active',
    priority: 3,
    createTime: '2025-12-01 10:00:00',
    updateTime: '2025-12-01 10:00:00',
  },
  {
    id: 4,
    name: '游戏攻略文章',
    code: 'game_strategy_article',
    category: 'text',
    description: '游戏攻略和技巧文章',
    template: 'strategy_article_v1',
    tags: ['游戏', '攻略', '技巧'],
    status: 'active',
    priority: 4,
    createTime: '2025-12-01 10:00:00',
    updateTime: '2025-12-01 10:00:00',
  },
  {
    id: 5,
    name: '赛事解说音频',
    code: 'match_commentary_audio',
    category: 'audio',
    description: '体育赛事解说音频',
    template: 'commentary_audio_v1',
    duration: 300,
    tags: ['体育', '解说', '音频'],
    status: 'active',
    priority: 5,
    createTime: '2025-12-01 10:00:00',
    updateTime: '2025-12-01 10:00:00',
  },
  {
    id: 6,
    name: '抽奖活动视频',
    code: 'lottery_activity_video',
    category: 'video',
    description: '抽奖活动宣传视频',
    template: 'lottery_video_v1',
    duration: 30,
    resolution: '1080x1920',
    aspectRatio: '9:16',
    tags: ['活动', '抽奖', '宣传'],
    status: 'active',
    priority: 6,
    createTime: '2025-12-01 10:00:00',
    updateTime: '2025-12-01 10:00:00',
  },
  {
    id: 7,
    name: '优惠活动海报',
    code: 'promotion_poster',
    category: 'image',
    description: '优惠活动宣传海报',
    template: 'promotion_poster_v1',
    resolution: '1080x1920',
    aspectRatio: '9:16',
    tags: ['优惠', '活动', '海报'],
    status: 'active',
    priority: 7,
    createTime: '2025-12-01 10:00:00',
    updateTime: '2025-12-01 10:00:00',
  },
  {
    id: 8,
    name: '每日新闻摘要',
    code: 'daily_news_summary',
    category: 'text',
    description: '每日新闻摘要文章',
    template: 'news_summary_v1',
    tags: ['新闻', '摘要', '每日'],
    status: 'active',
    priority: 8,
    createTime: '2025-12-01 10:00:00',
    updateTime: '2025-12-01 10:00:00',
  },
  {
    id: 9,
    name: '评论内容',
    code: 'comment',
    category: 'text',
    description: '评论内容，用于在视频、帖子下进行互动',
    template: 'comment_v1',
    tags: ['评论', '互动', '社交'],
    status: 'active',
    priority: 9,
    createTime: '2025-12-01 10:00:00',
    updateTime: '2025-12-01 10:00:00',
  },
];

const ContentTypeConfig: React.FC = () => {
  const [data, setData] = useState<ContentType[]>(initialData);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<ContentType | null>(null);
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: ContentType) => {
    setEditingRecord(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = (id: number) => {
    setData(prev => prev.filter(item => item.id !== id));
    message.success('删除成功');
  };

  const handleSave = () => {
    form.validateFields().then(values => {
      if (editingRecord) {
        // 编辑
        setData(prev =>
          prev.map(item =>
            item.id === editingRecord.id
              ? { ...item, ...values, updateTime: new Date().toLocaleString('zh-CN') }
              : item
          )
        );
        message.success('更新成功');
      } else {
        // 新增
        const newRecord: ContentType = {
          id: Date.now(),
          ...values,
          status: 'active',
          createTime: new Date().toLocaleString('zh-CN'),
          updateTime: new Date().toLocaleString('zh-CN'),
        };
        setData(prev => [...prev, newRecord]);
        message.success('新增成功');
      }
      setModalVisible(false);
      form.resetFields();
    });
  };

  const filteredData = data.filter(item =>
    item.name.includes(searchText) ||
    item.code.includes(searchText) ||
    item.description.includes(searchText)
  );

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '内容名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
    },
    {
      title: '内容编码',
      dataIndex: 'code',
      key: 'code',
      width: 180,
    },
    {
      title: '内容类型',
      dataIndex: 'category',
      key: 'category',
      width: 100,
      render: (category: string) => {
        const map: Record<string, { text: string; color: string }> = {
          video: { text: '视频', color: 'red' },
          image: { text: '图片', color: 'blue' },
          text: { text: '文本', color: 'green' },
          audio: { text: '音频', color: 'orange' },
        };
        const info = map[category] || { text: category, color: 'default' };
        return <Tag color={info.color}>{info.text}</Tag>;
      },
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      ellipsis: true,
    },
    {
      title: '模板',
      dataIndex: 'template',
      key: 'template',
      width: 150,
    },
    {
      title: '标签',
      dataIndex: 'tags',
      key: 'tags',
      width: 200,
      render: (tags: string[]) => (
        <Space>
          {tags.map(tag => (
            <Tag key={tag}>{tag}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      sorter: (a: ContentType, b: ContentType) => a.priority - b.priority,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={status === 'active' ? 'success' : 'default'}>
          {status === 'active' ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: ContentType) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确认删除？"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div className="page-header">
        <h1>内容类型配置</h1>
        <p>配置AI生成的内容类型，包括视频、图片、文本、音频等</p>
      </div>

      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Input
            placeholder="搜索内容名称、编码或描述"
            prefix={<SearchOutlined />}
            style={{ width: 300 }}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            新增
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => message.info('刷新成功')}>
            刷新
          </Button>
        </Space>

        <Table
          rowKey="id"
          dataSource={filteredData}
          columns={columns}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1500 }}
        />
      </Card>

      <Modal
        title={editingRecord ? '编辑内容类型' : '新增内容类型'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={handleSave}
        width={800}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="内容名称"
            rules={[{ required: true, message: '请输入内容名称' }]}
          >
            <Input placeholder="如：抖奶擦边视频" />
          </Form.Item>
          <Form.Item
            name="code"
            label="内容编码"
            rules={[{ required: true, message: '请输入内容编码' }]}
          >
            <Input placeholder="如：bounce_edge_video" />
          </Form.Item>
          <Form.Item
            name="category"
            label="内容类型"
            rules={[{ required: true, message: '请选择内容类型' }]}
          >
            <Select placeholder="请选择内容类型">
              <Option value="video">视频</Option>
              <Option value="image">图片</Option>
              <Option value="text">文本</Option>
              <Option value="audio">音频</Option>
              <Option value="comment">评论</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="description"
            label="描述"
            rules={[{ required: true, message: '请输入描述' }]}
          >
            <TextArea rows={3} placeholder="描述该内容类型的用途和特点" />
          </Form.Item>
          <Form.Item name="template" label="模板">
            <Input placeholder="如：bounce_template_v1" />
          </Form.Item>
          <Form.Item name="duration" label="时长（秒）">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="resolution" label="分辨率">
            <Input placeholder="如：1080x1920" />
          </Form.Item>
          <Form.Item name="aspectRatio" label="宽高比">
            <Input placeholder="如：9:16" />
          </Form.Item>
          <Form.Item name="priority" label="优先级" initialValue={0}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="status" label="状态" initialValue="active">
            <Radio.Group>
              <Radio value="active">启用</Radio>
              <Radio value="inactive">禁用</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ContentTypeConfig;

